import { createApi } from '@reduxjs/toolkit/query/react';
import { prepareBaseQuery } from '../shared/configs/config';

export type AgingBucket = '0-30' | '31-60' | '61-90' | '90+' | 'unmailed';

export interface CheckHistoryEntry {
  check_number: string;
  check_status: string | null;
  check_mailed_on: string | null;
  check_paid_on: string | null;
}

export interface BuyerCheckAgingRow {
  transfer_id: string;
  payup_transfer_number: number;
  amount: string;
  property_id: string;
  property_name: string | null;
  account_id: string | null;
  vendor_name: string | null;
  approved_at: string | null;
  created_at: string;
  current_check_number: string | null;
  current_check_status: string | null;
  current_check_image_url: string | null;
  current_check_mailed_on: string | null;
  earliest_mailed_on: string | null;
  check_count: number;
  check_history: CheckHistoryEntry[];
  reissued: boolean;
  days_outstanding: number;
  aging_bucket: AgingBucket;
}

export interface BucketSummary {
  count: number;
  total_amount: number;
}

export interface BuyerCheckAgingResponse {
  rows: BuyerCheckAgingRow[];
  summary: Record<AgingBucket, BucketSummary>;
}

export type PayMode = 'ach' | 'check' | 'debit_card' | 'digital_check';

export interface BuyerRefundRow {
  transfer_id: string;
  payup_transfer_number: number;
  batch_number: number | null;
  amount: string;
  pay_mode: PayMode;
  property_id: string;
  property_name: string | null;
  account_id: string | null;
  vendor_name: string | null;
  created_at: string;
  approved_at: string | null;
  paid_at: string | null;
  check_number: string | null;
  check_mailed_on: string | null;
  refunded_at: string | null;
  refund_notes: string | null;
  refunded_by_user_id: string | null;
  refunded_by_user_name: string | null;
  notes: string | null;
}

export interface PayModeBucket {
  count: number;
  total_amount: number;
}

export interface BuyerRefundsResponse {
  rows: BuyerRefundRow[];
  summary: {
    count: number;
    total_amount: number;
    by_pay_mode: Partial<Record<PayMode, PayModeBucket>>;
  };
}

export interface BuyerCancelRow {
  transfer_id: string;
  payup_transfer_number: number;
  batch_number: number | null;
  amount: string;
  pay_mode: PayMode;
  property_id: string;
  property_name: string | null;
  account_id: string | null;
  vendor_name: string | null;
  created_at: string;
  approved_at: string | null;
  check_number: string | null;
  canceled_at: string | null;
  cancellation_notes: string | null;
  canceled_by_user_id: string | null;
  canceled_by_user_name: string | null;
  notes: string | null;
}

export interface BuyerCancellationsResponse {
  rows: BuyerCancelRow[];
  summary: {
    count: number;
    total_amount: number;
    by_pay_mode: Partial<Record<PayMode, PayModeBucket>>;
  };
}

export interface PendingPayableRow {
  id: string;
  propertyId: string;
  propertyName: string;
  coupaSupplierId: string | null;
  identityKey: string | null;
  canInvite: boolean;
  vendorName: string;
  vendorCode: string | null;
  ein: string | null;
  amount: number;
  invoiceNo: string | null;
  dueDate: string | null;
  daysPending: number;
  firstSeenAt: string;
  // Held state: true once funds have been collected into a hold-charge and the
  // payable is waiting on the vendor to register to be paid out.
  isHeld: boolean;
  heldAmount: number | null;
  chargedAt: string | null;
  daysHeld: number | null;
}

export interface PendingPayablesResponse {
  rows: PendingPayableRow[];
}

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  tagTypes: ['Reports'],
  baseQuery: prepareBaseQuery(),
  endpoints: (builder) => ({
    getBuyerCheckAging: builder.query<BuyerCheckAgingResponse, void>({
      query: () => ({
        url: `/reports/buyer-check-aging`,
        method: 'GET',
      }),
      providesTags: ['Reports'],
    }),
    getBuyerRefunds: builder.query<BuyerRefundsResponse, void>({
      query: () => ({
        url: `/reports/refunds`,
        method: 'GET',
      }),
      providesTags: ['Reports'],
    }),
    getBuyerCancellations: builder.query<BuyerCancellationsResponse, void>({
      query: () => ({
        url: `/reports/cancellations`,
        method: 'GET',
      }),
      providesTags: ['Reports'],
    }),
    getPendingPayables: builder.query<PendingPayablesResponse, { propertyId?: string } | void>({
      query: (params) => ({
        url: `/reports/pending-payables`,
        method: 'GET',
        params: params && params.propertyId ? { propertyId: params.propertyId } : undefined,
      }),
      providesTags: ['Reports'],
    }),
  }),
});

export const {
  useGetBuyerCheckAgingQuery,
  useGetBuyerRefundsQuery,
  useGetBuyerCancellationsQuery,
  useGetPendingPayablesQuery,
} = reportsApi;
